// src/App.js
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { onAuthStateChanged, signOut, signInWithEmailAndPassword, updateProfile, sendPasswordResetEmail, signInAnonymously } from 'firebase/auth';
import { doc, updateDoc, serverTimestamp, getFirestore, collection, query, where, onSnapshot } from 'firebase/firestore'; 
import { 
    auth, registerUserCallable, getMeetingByInviteTokenCallable, 
    createCompanyCallable, setSuperAdminClaimCallable, getAllCompaniesAndUsersCallable,
    getNextMeetingNumber,
    getExternalMeetingDetailsCallable,
    proposeAgendaItemCallable,
    reviewAgendaItemCallable,
    createTranslationJobCallable, 
    cancelTranslationJobCallable,
    getTranslationJobDataCallable,
    submitTranslationCallable,
    db 
} from './services/firebase';
import {
  getMeetings, 
  saveMeeting,          
  getMeetingMaster,     
  getMeetingContent,    
  deleteMeeting,
  getUserProfile, saveUserProfile,
  getTasks, saveTask,
  getCompanyUsers, getTeams,
  getCrossCompanyMeetings,
  sendGuestMeetingInvites,
  createNotification,
  getUserNotifications,
  getCompanyRegistrationRequests,
  markNotificationsForMeetingAsRead,
  updateParticipantStatus,
  getAgendaProposals,
  proposeAgendaItem,
  reviewAgendaItem
} from './services/firestoreService';
import { getCompanyIdFromSubdomain, generateId } from './services/utils';

// Import der Komponenten
import Notification from './components/Notification';
import AuthModal from './components/AuthModal';
import AdminDashboard from './components/AdminDashboard';
import MeetingList from './components/MeetingList';
import MeetingForm from './components/MeetingForm';
import MeetingView from './components/MeetingView';
import SuperAdminDashboard from './components/SuperAdminDashboard';
import CompanyRequestModal from './components/CompanyRequestModal';
import Dashboard from './components/Dashboard';
import CustomModal from './components/CustomModal';
import UserProfilePage from './components/UserProfilePage';
import TaskDashboard from './components/TaskDashboard';
import AdminTeamManagement from './components/AdminTeamManagement';
import PublicDashboard from './components/PublicDashboard';
import NotificationBell from './components/NotificationBell';
import TranslationPortal from './components/TranslationPortal'; 
import ProjectTopics from './components/ProjectTopics'; 
import CalendarPage from './components/CalendarPage';
import { PricingView, PurchasingModule } from './components/PurchasingExtensions';
import { Articles } from './components/Articles';


import './i18n-config'; 
import { useTranslation } from 'react-i18next';

const IDLE_TIMEOUT_MS = 30 * 60 * 1000;

// --- Helper Funktionen ---

function hydrateI18nAgenda(agendaStructure, content) {
    if (!Array.isArray(agendaStructure) || !content) return [];
    const hydrateItems = (items) => {
        return (items || []).map(item => {
            const description = content[`item_${item.id}_desc`] || "Agendapunkt nicht gefunden";
            const notes = content[`item_${item.id}_notes`] || "";
            return { ...item, description, notes, subPoints: hydrateItems(item.subPoints || []) };
        });
    };
    return hydrateItems(agendaStructure);
}

function hydrateTasksInAgenda(agendaItems, taskListToUse) {
    const taskList = Array.isArray(taskListToUse) ? taskListToUse : [];
    if (!Array.isArray(agendaItems)) return [];
    return agendaItems.map(item => {
        const validItem = item && typeof item === 'object';
        if (!validItem) return null;
        const validTaskIds = Array.isArray(item.taskIds) ? item.taskIds : [];
        const hydratedTasks = validTaskIds.map(id => {
            const foundTask = taskList.find(task => task && task.id === id);
            return foundTask;
        }).filter(Boolean);
        const tasksByAgendaId = taskList.filter(t => t.agendaItemId === item.id);
        tasksByAgendaId.forEach(task => {
            if (!hydratedTasks.some(ht => ht.id === task.id)) {
                hydratedTasks.push(task);
            }
        });
        return {
            ...item,
            tasks: hydratedTasks,
            subPoints: Array.isArray(item.subPoints) ? hydrateTasksInAgenda(item.subPoints, taskListToUse) : []
        };
    }).filter(Boolean);
}

async function processMeetingTasksAndGetIdAgenda(agendaItems, meeting, tasks, teams, companyId, userId, userProfile, setNotification) {
    const currentTasksState = Array.isArray(tasks) ? tasks : [];
    const savedTasks = [];
    
    const processItemsRecursively = async (items) => {
        if (!Array.isArray(items)) return [];
        const processedItems = [];
        for (const item of items) {
            const newItem = { ...item, taskIds: [] };
            if (Array.isArray(item.tasks)) {
                for (const task of item.tasks) {
                    const taskId = task.id || generateId();
                    const taskWithId = { ...task, id: taskId };
                    const oldTask = currentTasksState.find(t => t.id === taskId);
                    const isNewTask = !oldTask || !oldTask.createdAt;
                    const timestamp = new Date().toISOString();
                    const userName = userProfile.firstName ? `${userProfile.firstName} ${userProfile.lastName}`.trim() : 'Unbekannter Benutzer';
                    
                    let taskToSave = { ...taskWithId };
                    taskToSave.meetingId = meeting.id;
                    taskToSave.meetingTitle = meeting.title;
                    taskToSave.agendaItemId = item.id;
                    taskToSave.isCompleted = taskToSave.status === 'Erledigt';
                    
                    let newHistoryEntries = [];
                    if (isNewTask) {
                        taskToSave.createdAt = timestamp;
                        taskToSave.creatorUserId = userId;
                        newHistoryEntries.push({ timestamp, user: userName, action: 'Aufgabe im Protokoll erstellt.' });
                        taskToSave.history = newHistoryEntries;
                    } else {
                        if (oldTask.title !== taskToSave.title) newHistoryEntries.push({ timestamp, user: userName, action: `Titel geändert.` });
                        if (oldTask.status !== taskToSave.status) newHistoryEntries.push({ timestamp, user: userName, action: `Status zu "${taskToSave.status}" geändert.` });
                        if (oldTask.isCompleted !== taskToSave.isCompleted) {
                            const actionText = taskToSave.isCompleted ? 'Als erledigt markiert.' : 'Als offen markiert.';
                            newHistoryEntries.push({ timestamp, user: userName, action: actionText });
                        }
                        taskToSave.history = newHistoryEntries.length > 0 ? [...(oldTask.history || []), ...newHistoryEntries] : (oldTask.history || []);
                    }
                    
                    const finalAssignee = taskToSave.assignees?.[0];
                    if (finalAssignee) {
                        if (finalAssignee.type === 'user') taskToSave.assigneeUids = [finalAssignee.id];
                        else if (finalAssignee.type === 'team') {
                            const team = teams.find(t => t.id === finalAssignee.id);
                            taskToSave.assigneeUids = team ? team.members : [];
                        } else { taskToSave.assigneeUids = []; }
                    } else { taskToSave.assigneeUids = []; }
                    
                    try {
                        await saveTask(taskToSave, companyId, userId);
                        savedTasks.push(taskToSave);
                    } catch (saveError) {
                        console.error(`Fehler beim Speichern von Task ${taskId}:`, saveError);
                    }
                    newItem.taskIds.push(taskId);
                }
            }
            delete newItem.tasks;
            if (item.subPoints) {
                newItem.subPoints = await processItemsRecursively(item.subPoints);
            }
            processedItems.push(newItem);
        }
        return processedItems;
    };
    
    const finalAgenda = await processItemsRecursively(agendaItems);
    return { finalAgenda, savedTasks };
}

async function handleSyncAttendees(meeting, userId) {
    if (!meeting || !meeting.id) return;
    const participantUids = (meeting.allParticipants || []).filter(p => p.uid && !p.isExternal && p.uid !== userId).map(p => p.uid);
    const notificationPromises = participantUids.map(uid => createNotification(uid, { ...meeting, isExternal: false }));
    try {
        await Promise.all(notificationPromises);
    } catch (error) {
        console.error("Error sending notifications in handleSyncAttendees:", error);
    }
}

// --- ICONS ---
const IconDashboard = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" /></svg>;
const IconProtocols = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" /></svg>;
const IconTasks = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;
const IconProjects = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z" /></svg>;
const IconMenu = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" /></svg>;
const IconCalendar = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}><path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5m-9-6h.008v.008H12v-.008zM12 15h.008v.008H12V15zm0 2.25h.008v.008H12v-.008zM9.75 15h.008v.008H9.75V15zm0 2.25h.008v.008H9.75v-.008zM7.5 15h.008v.008H7.5V15zm0 2.25h.008v.008H7.5v-.008zm6.75-4.5h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008V15zm0 2.25h.008v.008h-.008v-.008zm2.25-4.5h.008v.008H16.5v-.008zm0 2.25h.008v.008H16.5V15z" /></svg>;
const IconShoppingCart = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 00-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 00-16.536-1.84M7.5 14.25L5.106 5.272M6 20.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zm12.75 0a.75.75 0 11-1.5 0 .75.75 0 011.5 0z" /></svg>;
const IconPackage = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}><path strokeLinecap="round" strokeLinejoin="round" d="M21 7.5l-9-5.25L3 7.5m18 0l-9 5.25m9-5.25v9l-9 5.25M3 7.5l9 5.25M3 7.5v9l9 5.25m0-9v9" /></svg>;

// --- Helper Component: Nav Item ---
const NavItem = ({ icon, label, onClick, collapsed, active }) => (
    <button 
        onClick={onClick}
        className={`w-full flex items-center p-3 mb-1 rounded-xl transition-all duration-200 group relative
            ${active 
                ? 'bg-blue-600 text-white shadow-md' 
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-blue-600 dark:hover:text-blue-400'
            }
        `}
        title={collapsed ? label : ''}
    >
        <div className={`flex-shrink-0 ${collapsed ? 'mx-auto' : 'mr-3'} transition-all duration-300`}>
            {React.cloneElement(icon, { className: "w-6 h-6" })}
        </div>
        <span className={`font-medium whitespace-nowrap overflow-hidden transition-all duration-300 origin-left ${collapsed ? 'w-0 opacity-0 scale-0' : 'w-auto opacity-100 scale-100'}`}>
            {label}
        </span>
    </button>
);

function App() {
  const today = new Date().toISOString().split('T')[0];
  const { t, i18n } = useTranslation(); 

  // --- STATE ---
  const [meetings, setMeetings] = useState([]);
  const [externalMeetings, setExternalMeetings] = useState([]);
  const [tasks, setTasks] = useState([]); 
  const [companyUsers, setCompanyUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [userProfile, setUserProfile] = useState({});
  const [userNotifications, setUserNotifications] = useState([]);
  const [registrationRequests, setRegistrationRequests] = useState([]); 
  const [agendaProposals, setAgendaProposals] = useState([]);
  const [projectTopics, setProjectTopics] = useState([]);

  const [userId, setUserId] = useState(null);
  const [userEmail, setUserEmail] = useState(null);
  const [userPhotoURL, setUserPhotoURL] = useState(null);
  const [companyId, setCompanyId] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [permissionError, setPermissionError] = useState(null);
  
  const [currentHash, setCurrentHash] = useState(window.location.hash || '#/dashboard');
  const [currentLang, setCurrentLang] = useState(i18n.language || 'de');
  
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [isLoading, setIsLoading] = useState(false); 
  const [isLoadingMeeting, setIsLoadingMeeting] = useState(false); 
  
  const [notification, setNotification] = useState({ message: '', type: null });
  const [isDarkMode, setIsDarkMode] = useState(false);
  
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  
  const [guestMeeting, setGuestMeeting] = useState(null);
  const [guestTasks, setGuestTasks] = useState([]);
  const [isGuestView, setIsGuestView] = useState(false);
  const [guestError, setGuestError] = useState(null);
  
  const [isRedirecting, setIsRedirecting] = useState(false);

  // === SIDEBAR STATES ===
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const meetingFormRef = useRef(null);
  const profileMenuRef = useRef(null);
  const idleTimerRef = useRef(null);
  
  useEffect(() => { i18n.changeLanguage(currentLang); }, [currentLang, i18n]);

  useEffect(() => {
    const savedMode = localStorage.getItem('theme');
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    if (savedMode === 'dark' || (savedMode === null && prefersDark)) {
      document.documentElement.classList.add('dark'); setIsDarkMode(true);
    } else {
      document.documentElement.classList.remove('dark'); localStorage.setItem('theme', 'light'); setIsDarkMode(false);
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setIsProfileMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const navigate = useCallback((path) => {
    if (window.location.hash !== path) {
      window.location.hash = path;
    }
    setIsMobileMenuOpen(false);
  }, []);

  const handleLogout = useCallback(async () => {
    setIsProfileMenuOpen(false);
    try {
      await signOut(auth);
      navigate('#/dashboard'); 
    } catch (error) {
        setNotification({ message: `Fehler beim Abmelden: ${error.message}`, type: 'error' });
    }
  }, [navigate]); 

  const toggleSidebar = () => {
      if (window.innerWidth >= 1024) {
          setIsSidebarCollapsed(!isSidebarCollapsed);
      } else {
          setIsMobileMenuOpen(!isMobileMenuOpen);
      }
  };

  useEffect(() => {
    const resetIdleTimer = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => {
        if (userId) handleLogout(); 
      }, IDLE_TIMEOUT_MS);
    };
    if (userId) {
      resetIdleTimer();
      ['mousedown', 'keydown', 'touchstart', 'scroll'].forEach(event => window.addEventListener(event, resetIdleTimer));
    }
    return () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      ['mousedown', 'keydown', 'touchstart', 'scroll'].forEach(event => window.removeEventListener(event, resetIdleTimer));
    };
  }, [userId, handleLogout]); 

  const toggleDarkMode = () => {
    const isDark = document.documentElement.classList.toggle('dark');
    setIsDarkMode(isDark); localStorage.setItem('theme', isDark ? 'dark' : 'light');
  };

  // Auth & Init
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const isLogoutRedirect = params.get('action') === 'logout';
    if (isLogoutRedirect) {
        window.history.replaceState({}, document.title, window.location.pathname);
    }
    const handleGuestInvitation = async () => {
      const token = params.get('token');
      if (token) {
        try {
            const currentUser = auth.currentUser;
            if (currentUser) {
                if (!currentUser.isAnonymous) {
                     await signOut(auth);
                     setUserId(null); 
                     await signInAnonymously(auth);
                }
            } else {
                await signInAnonymously(auth);
            }
        } catch (e) { 
            console.error('Guest Auth Error:', e);
            if (e.code === 'auth/operation-not-allowed') {
                setGuestError("Fehler: Gast-Zugang ist deaktiviert. Bitte 'Anonymous' Login in Firebase Console aktivieren.");
                return true;
            }
            if (e.code === 'auth/admin-restricted-operation') {
                setGuestError("Fehler: Anonyme Anmeldung ist in Firebase deaktiviert. Bitte aktivieren Sie 'Anonymous' im Abschnitt 'Sign-in method' der Firebase Console.");
                return true; 
            }
        }

        setIsGuestView(true);
        try {
          window.history.replaceState({}, document.title, window.location.pathname);
          const result = await getMeetingByInviteTokenCallable({ token });
          if (result.data && result.data.meeting) {
             setGuestMeeting(result.data.meeting);
             setGuestTasks(result.data.allTasks || []);
             setGuestError(null);
             setCurrentLang(result.data.meeting.originalLanguage || 'de');
          } else {
             setGuestMeeting(null);
             setGuestError("Der Link ist gültig, aber das Protokoll konnte nicht geladen werden.");
          }
        } catch (error) {
          console.error("Guest Load Error:", error);
          if (error.code === 'permission-denied' || error.message.includes('permission')) {
              setGuestError("Zugriff verweigert: Bitte prüfen Sie, ob die anonyme Anmeldung erfolgreich war.");
          } else if (error.message && (error.message.includes('Could not retrieve') || error.message.includes('INTERNAL'))) {
              setGuestError("Das Protokoll konnte nicht geladen werden. Der Link ist möglicherweise abgelaufen oder ungültig.");
          } else {
              setGuestError(`Fehler beim Laden: ${error.message || 'Unbekannter Fehler'}`);
          }
          setGuestMeeting(null);
        } finally {
          setIsAuthReady(true);
        }
        return true; 
      }
      return false; 
    };
    handleGuestInvitation().then(isGuest => {
        if (!isGuest) {
            const mainDomain = 'meetform.de';
            const hostname = window.location.hostname;
            const currentCompanyIdFromSubdomain = getCompanyIdFromSubdomain();
            setCompanyId(currentCompanyIdFromSubdomain);
            const authUnsubscribe = onAuthStateChanged(auth, async (user) => {
                if (isLogoutRedirect && user) {
                    signOut(auth); return; 
                }
                if (user && !user.isAnonymous) { 
                    setPermissionError(null);
                    setIsAuthReady(false); 
                    try {
                        await user.getIdToken(true);
                        const idTokenResult = await user.getIdTokenResult();
                        const claims = idTokenResult.claims || {};
                        const userCompanyId = claims.companyId;
                        const userRole = claims.role;
                        const userIsSuperAdmin = claims.superAdmin === true;
                        setShowAuthModal(false);
                        if (userCompanyId && !userIsSuperAdmin) {
                            const db = getFirestore();
                            const userRef = doc(db, `companies/${userCompanyId}/users/${user.uid}`);
                            updateDoc(userRef, { lastLoginAt: serverTimestamp() }).catch(e => console.error("Konnte Login-Zeitstempel nicht aktualisieren:", e));
                        }
                        if (!hostname.includes('localhost') && !isLogoutRedirect) {
                            if (!currentCompanyIdFromSubdomain && userCompanyId && !userIsSuperAdmin) {
                                setIsRedirecting(true); window.location.href = `https://${userCompanyId}.${mainDomain}`; return;
                            }
                            if (currentCompanyIdFromSubdomain && userCompanyId && currentCompanyIdFromSubdomain !== userCompanyId) {
                                setIsRedirecting(true); window.location.href = `https://${userCompanyId}.${mainDomain}`; return;
                            }
                        }
                        setUserId(user.uid); setUserEmail(user.email); setUserPhotoURL(user.photoURL);
                        setUserRole(userRole || 'user');
                        setIsSuperAdmin(userIsSuperAdmin);
                        if (userIsSuperAdmin && !currentCompanyIdFromSubdomain) {
                            setCurrentLang('de'); 
                        } else if (!userCompanyId || userCompanyId !== currentCompanyIdFromSubdomain) {
                            let errorMsg = !userCompanyId ? `Fehler: Ihrem Konto ist keine Firma zugewiesen...` : `Sie haben keine Berechtigung für "${currentCompanyIdFromSubdomain}"...`;
                            setPermissionError(errorMsg);
                        } else if (!userIsSuperAdmin) {
                            const profileData = await getUserProfile(currentCompanyIdFromSubdomain, user.uid);
                            setUserProfile(profileData || { firstName: user.displayName?.split(' ')[0] || '', lastName: user.displayName?.split(' ')[1] || '', email: user.email });
                            setCurrentLang(profileData?.preferredLanguage || 'de');
                        }
                    } catch (error) {
                        setNotification({ message: `Fehler beim Laden der Benutzerdaten: ${error.message}`, type: 'error' });
                        setPermissionError(`Ein Fehler ist aufgetreten: ${error.message}`);
                    } finally {
                        setIsAuthReady(true); 
                    }
                } else {
                    setUserId(null); setUserEmail(null); setUserPhotoURL(null); setUserRole(null);
                    setIsSuperAdmin(false); setUserProfile({});
                    setMeetings([]); setExternalMeetings([]);
                    setTasks([]); setCompanyUsers([]); setTeams([]); setPermissionError(null);
                    setUserNotifications([]);
                    setCompanyId(getCompanyIdFromSubdomain()); 
                    setIsAuthReady(true);
                    setCurrentLang('de'); 
                }
            });
            return () => { if (authUnsubscribe) authUnsubscribe(); };
        }
    });
    return () => {};
  }, []); 

  // Data Loading
  useEffect(() => {
    let unsubscribes = [];
    const shouldLoadData = userId && companyId && !isGuestView && !isRedirecting && !permissionError;
    const shouldLoadSuperAdminData = userId && isSuperAdmin && !companyId && !isGuestView && !isRedirecting;
    if (shouldLoadData) {
        unsubscribes.push(getMeetings(companyId, userId, setMeetings));
        unsubscribes.push(getCrossCompanyMeetings(userId, setExternalMeetings));
        unsubscribes.push(getCompanyUsers(companyId, setCompanyUsers));
        unsubscribes.push(getTeams(companyId, setTeams));
        unsubscribes.push(getTasks(companyId, userId, userRole || 'user', setTasks));
        unsubscribes.push(getUserNotifications(userId, setUserNotifications));
        getUserProfile(companyId, userId).then(profileData => {
             if (profileData) {
                setUserProfile(profileData);
                setCurrentLang(profileData.preferredLanguage || 'de');
             }
        });
    } else if (shouldLoadSuperAdminData) {
        unsubscribes.push(getCompanyRegistrationRequests(setRegistrationRequests));
    } else {
        setMeetings([]); setExternalMeetings([]); setCompanyUsers([]);
        setTeams([]); setTasks([]); setUserNotifications([]); setRegistrationRequests([]);
    }
    return () => { unsubscribes.forEach(unsub => { if (unsub) unsub(); }); };
  }, [userId, companyId, userRole, isGuestView, isRedirecting, permissionError, isSuperAdmin]);

  useEffect(() => {
    if (notification.message) {
      const timer = setTimeout(() => setNotification({ message: '', type: null }), 3000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  // Project Topics Loader
  useEffect(() => {
      if (selectedMeeting && selectedMeeting.projectId && companyId) {
          const q = query(
              collection(db, "companies", companyId, "projectTopics"), 
              where("projectId", "==", selectedMeeting.projectId),
              where("status", "==", "open")
          );
          const unsubscribe = onSnapshot(q, (snapshot) => {
              const loadedTopics = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
              setProjectTopics(loadedTopics);
          }, (error) => {
              console.error("Fehler beim Laden der Projekt-Themen:", error);
          });
          return () => unsubscribe();
      } else {
          setProjectTopics([]);
      }
  }, [selectedMeeting, companyId]);

  const { view, selectedId, token } = useMemo(() => {
    const hash = currentHash.split('?')[0]; 
    const parts = hash.substring(2).split('/'); 
    const newView = parts[0] || 'dashboard';
    const newId = parts[1] || null;
    
    if(newView === 'translate' && newId) return { view: 'translate', selectedId: null, token: newId };
    if(newView === 'protokoll' && newId) return { view: 'view', selectedId: newId, token: null };
    if(newView === 'form' && newId) return { view: 'form', selectedId: newId, token: null };
    if(newView === 'protokolle') return { view: 'list', selectedId: null, token: null };
    if(newView === 'aufgaben') return { view: 'tasks', selectedId: null, token: null };
    if(newView === 'projekte') return { view: 'projects', selectedId: null, token: null };
    if(newView === 'kalender') return { view: 'calendar', selectedId: null, token: null }; 
    if(newView === 'artikel') return { view: 'artikel', selectedId: null, token: null }; 
    if(newView === 'einkauf') return { view: 'einkauf', selectedId: null, token: null }; 
    if(newView === 'pricing') return { view: 'pricing', selectedId: null, token: null }; 
    
    if(newView === 'profil') return { view: 'profile', selectedId: null, token: null };
    if(newView === 'admin') return { view: 'admin', selectedId: null, token: null };
    if(newView === 'super-admin') return { view: 'superAdmin', selectedId: null, token: null };
    return { view: 'dashboard', selectedId: null, token: null };
  }, [currentHash]);

  const loadMeetingData = useCallback(async (meetingFromList, mode) => {
      if (!meetingFromList) return;
      const { id: meetingId, companyId: meetingCompanyId, originalLanguage } = meetingFromList;
      if (userId && meetingId) { markNotificationsForMeetingAsRead(userId, meetingId); }
      try {
          setIsLoadingMeeting(true);
          const masterData = await getMeetingMaster(meetingCompanyId, meetingId);
          if (!masterData) throw new Error("Protokoll-Master-Dokument nicht gefunden.");
          const originalContent = await getMeetingContent(meetingCompanyId, meetingId, originalLanguage || 'de');
          let targetContent = {};
          if (currentLang !== (originalLanguage || 'de')) { targetContent = await getMeetingContent(meetingCompanyId, meetingId, currentLang); }
          let tasksForThisMeeting = tasks;
          if (meetingFromList.isExternal) {
              const result = await getExternalMeetingDetailsCallable({ companyId: meetingCompanyId, meetingId: meetingId, targetLanguage: currentLang });
              if (!result.data || !result.data.meeting) throw new Error("Externes Protokoll konnte nicht geladen werden.");
              Object.assign(masterData, result.data.meeting); tasksForThisMeeting = result.data.allTasks || [];
          }
          let combinedMeeting;
          if (!meetingFromList.isExternal) {
              combinedMeeting = { ...masterData, ...originalContent, ...targetContent, id: masterData.id, companyId: masterData.companyId, agenda_structure: masterData.agenda_structure || [] };
          } else { combinedMeeting = masterData; }
          const hydratedAgenda = hydrateI18nAgenda(combinedMeeting.agenda_structure, combinedMeeting);
          combinedMeeting.agenda = hydrateTasksInAgenda(hydratedAgenda, tasksForThisMeeting);
          combinedMeeting.langLoaded = currentLang;
          setSelectedMeeting(combinedMeeting); setIsLoadingMeeting(false);
      } catch (error) {
          console.error(`Fehler beim Laden des Protokolls:`, error);
          setNotification({ message: `Fehler beim Öffnen: ${error.message}.`, type: 'error' });
          setIsLoadingMeeting(false); navigate('#/protokolle'); 
      }
  }, [userId, companyId, tasks, currentLang, navigate]); 

  useEffect(() => {
    const handleHashChange = () => {
        const newHash = window.location.hash.split('?')[0] || '#/dashboard';
        const oldHash = currentHash.split('?')[0] || '#/dashboard';
        if(newHash !== oldHash) { setCurrentHash(window.location.hash || '#/dashboard'); }
    };
    window.addEventListener('hashchange', handleHashChange);
    let proposalsUnsubscribe = null;
    if (selectedMeeting && selectedMeeting.id && selectedMeeting.companyId && !selectedMeeting.isNew) {
        proposalsUnsubscribe = getAgendaProposals(selectedMeeting.companyId, selectedMeeting.id, setAgendaProposals);
    } else { setAgendaProposals([]); }
    const loadContent = async () => {
        if (!isAuthReady) return; 
        if (view === 'translate' && token) { setIsLoadingMeeting(false); setSelectedMeeting(null); return; }
        if (view === 'list' || view === 'dashboard' || view === 'tasks' || view === 'projects' || view === 'admin' || view === 'profile' || view === 'superAdmin' || view === 'calendar' || view === 'artikel' || view === 'einkauf' || view === 'pricing') { setSelectedMeeting(null); setIsLoadingMeeting(false); }
        if ((view === 'view' || view === 'form') && selectedId) {
            if (selectedMeeting && selectedMeeting.id === selectedId && selectedMeeting.langLoaded === currentLang) { setIsLoadingMeeting(false); return; }
            const allLoadedMeetings = [...meetings, ...externalMeetings];
            if (selectedMeeting && selectedMeeting.id === selectedId) { setIsLoadingMeeting(true); await loadMeetingData(selectedMeeting, view); return; }
            if (companyId && allLoadedMeetings.length === 0 && !(selectedMeeting && selectedMeeting.id === selectedId)) { setIsLoadingMeeting(true); return; }
            setIsLoadingMeeting(true);
            const meetingFromList = allLoadedMeetings.find(m => m.id === selectedId);
            if (meetingFromList) { await loadMeetingData(meetingFromList, view); } else { setNotification({ message: `Protokoll mit ID ${selectedId} nicht gefunden oder kein Zugriff.`, type: 'error' }); setIsLoadingMeeting(false); navigate('#/protokolle'); }
        }
    };
    loadContent();
    return () => { window.removeEventListener('hashchange', handleHashChange); if (proposalsUnsubscribe) { proposalsUnsubscribe(); } };
  }, [view, selectedId, token, isAuthReady, meetings, externalMeetings, tasks, loadMeetingData, navigate, currentHash, selectedMeeting, companyId, currentLang]); 

  // --- ACTIONS ---
  const handleGoToDashboard = () => navigate('#/dashboard');
  const handleGoToMeetingList = () => navigate('#/protokolle');
  const handleGoToTasks = () => navigate('#/aufgaben');
  const handleGoToProjects = () => navigate('#/projekte');
  const handleGoToCalendar = () => navigate('#/kalender'); 
  const handleGoToArtikel = () => navigate('#/artikel'); 
  const handleGoToEinkauf = () => navigate('#/einkauf');
  const handleGoToProfile = () => navigate('#/profil');
  const handleGoToAdminDashboard = () => navigate('#/admin');
  const handleGoToSuperAdminDashboard = () => navigate('#/super-admin');
  const handleBackToList = () => { setSelectedMeeting(null); navigate('#/protokolle'); };
  const handleSelectMeeting = (meeting) => { navigate(`#/form/${meeting.id}`); };
  const handleViewMeeting = (meeting) => { navigate(`#/protokoll/${meeting.id}`); };
  const handleRegister = async (email, password) => { setShowAuthModal(false); setShowRequestModal(true); throw new Error(t('auth.registerError', 'Bitte fordern Sie einen Firmen-Account an.')); };
  const handleLogin = (email, password) => signInWithEmailAndPassword(auth, email, password);
  
  const handleUpdateProfile = async (userToUpdate, profileData) => {
    if (!companyId) return;
    try {
        const displayName = `${profileData.firstName} ${profileData.lastName}`.trim();
        if(auth.currentUser && auth.currentUser.uid === userToUpdate.uid) { await updateProfile(auth.currentUser, { displayName, photoURL: profileData.photoURL }); setUserPhotoURL(profileData.photoURL); }
        await saveUserProfile(companyId, userToUpdate.uid, profileData);
        if(auth.currentUser && auth.currentUser.uid === userToUpdate.uid) { const updatedProfile = await getUserProfile(companyId, userToUpdate.uid); setUserProfile(updatedProfile || {}); if (updatedProfile.preferredLanguage) { setCurrentLang(updatedProfile.preferredLanguage); } }
        setNotification({ message: t('profile.updateSuccess', 'Profil erfolgreich aktualisiert!'), type: 'success' });
    } catch (error) { setNotification({ message: `${t('profile.updateError', 'Fehler')}: ${error.message}`, type: 'error'}); }
  };
  
  const handleSaveMeeting = async (meetingToSave, goToListAfterSave = false) => {
    if (!userId || !companyId || !currentLang) return; 
    try {
      setNotification({ message: "Speichere Protokoll...", type: 'info' });
      let processedMeeting = { ...meetingToSave, companyId: companyId, companyName: userProfile.companyName || companyId, status: meetingToSave.status || 'forecast' };
      const { finalAgenda, savedTasks } = await processMeetingTasksAndGetIdAgenda(processedMeeting.agenda, processedMeeting, tasks, teams, companyId, userId, userProfile, setNotification);
      if (processedMeeting.isNew) { processedMeeting.isNew = false; }
      await saveMeeting(processedMeeting, companyId, userId, currentLang);
      if (savedTasks.length > 0) { setTasks(currentTasks => { const savedTasksMap = new Map(savedTasks.map(t => [t.id, t])); const updatedTasks = currentTasks.map(t => savedTasksMap.has(t.id) ? savedTasksMap.get(t.id) : t); savedTasks.forEach(st => { if (!currentTasks.some(t => t.id === st.id)) { updatedTasks.push(st); } }); return updatedTasks; }); }
      if (processedMeeting.status === 'forecast') { await handleSyncAttendees(processedMeeting, userId); }
      setNotification({ message: `Protokoll erfolgreich gespeichert! Status: ${processedMeeting.status === 'completed' ? 'Abgeschlossen' : 'Forecast'}`, type: 'success' });
      if (goToListAfterSave) { navigate('#/protokolle'); } else { setTasks(currentTasks => { const rehydratedMeeting = { ...processedMeeting, agenda: hydrateTasksInAgenda(finalAgenda, currentTasks) }; setSelectedMeeting(rehydratedMeeting); return currentTasks; }); }
    } catch (error) { console.error('Fehler beim Speichern des Protokolls:', error); setNotification({ message: `Fehler beim Speichern des Protokolls! ${error.message}`, type: 'error' }); }
  };
  
  const handleSendInvites = async (meeting, validityHours = 48) => {
    if (!userId || !companyId) return setNotification({ message: 'Fehler: Nicht angemeldet.', type: 'error' });
    const guestParticipants = (meeting.allParticipants || []).filter(p => p.isExternal && p.email);
    if (guestParticipants.length === 0) return setNotification({ message: 'Keine externen Teilnehmer gefunden.', type: 'info' });
    try { setNotification({ message: `Sende Einladungen...`, type: 'info' }); const result = await sendGuestMeetingInvites(meeting, validityHours); if (result && result.success) { setNotification({ message: `Einladungen erfolgreich gesendet!`, type: 'success' }); } else { setNotification({ message: `Einladungen konnten nicht gesendet werden: ${result?.message}`, type: 'error' }); } } catch (error) { setNotification({ message: `Fehler beim Senden: ${error.message}.`, type: 'error' }); }
  };
  
  const handlePasswordReset = async (email) => { try { await sendPasswordResetEmail(auth, email); setNotification({ message: 'E-Mail zum Zurücksetzen gesendet!', type: 'success' }); return { success: true }; } catch (error) { setNotification({ message: 'Fehler beim Senden.', type: 'error' }); return { success: false, error }; } };
  const handleDeleteMeeting = async (id) => { if (!userId || !companyId) return setNotification({ message: 'Fehler: Nicht angemeldet.', type: 'error' }); try { setNotification({ message: "Lösche Protokoll...", type: 'info' }); await deleteMeeting(id, companyId); setNotification({ message: 'Protokoll erfolgreich gelöscht!', type: 'success' }); if (selectedMeeting && selectedMeeting.id === id) { navigate('#/protokolle'); } } catch (error) { setNotification({ message: `Fehler beim Löschen: ${error.message}`, type: 'error' }); } };
  const handleCreateNew = async () => {
    if (!userId) { setNotification({ message: 'Bitte anmelden.', type: 'info' }); setShowAuthModal(true); setAuthMode('login'); return; }
    setIsLoading(true); setNotification({ message: 'Erzeuge Protokollnummer...', type: 'info' });
    let meetingNumber = null;
    try { const { data } = await getNextMeetingNumber({ companyId }); meetingNumber = data.number; setNotification({ message: `Protokoll P-${meetingNumber} wird erstellt...`, type: 'success' }); } catch (error) { setNotification({ message: 'Fehler: Konnte keine Protokollnummer abrufen.', type: 'error' }); setIsLoading(false); return; }
    const creatorParticipant = { id: generateId(), uid: userId, name: userProfile.firstName ? `${userProfile.firstName} ${userProfile.lastName}`.trim() : userEmail || 'Unbekannt', email: userEmail || '', photoURL: userPhotoURL || '', isExternal: false, currentStatus: 'present', events: [] };
    const newMeetingId = generateId();
    const newMeeting = { id: newMeetingId, companyId: companyId, date: today, startTime: new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }), endTime: '', location: '', allParticipants: [creatorParticipant], participantUids: [userId], moderatorUid: userId, creatorUserId: userId, status: 'forecast', sequentialNumber: meetingNumber, originalLanguage: currentLang, availableLanguages: [currentLang], agenda_structure: [], agenda: [], title: '', isNew: true, langLoaded: currentLang };
    setSelectedMeeting(newMeeting); setIsLoading(false); navigate(`#/form/${newMeeting.id}`);
  };
  const handleDuplicateMeeting = async (originalMeeting, includeCompleted) => { setNotification({ message: 'Fehler: Duplizieren ist noch nicht implementiert.', type: 'error' }); };
  const handleUpdateParticipantStatus = async (meetingId, participantId, newStatus) => {
    if (!selectedMeeting) return;
    try { setNotification({ message: 'Status wird aktualisiert...', type: 'info' }); const updatedParticipants = await updateParticipantStatus(selectedMeeting.companyId, meetingId, participantId, newStatus); setSelectedMeeting(prevMeeting => ({ ...prevMeeting, allParticipants: updatedParticipants })); setNotification({ message: 'Status erfolgreich aktualisiert!', type: 'success' }); } catch (error) { setNotification({ message: `Status-Update fehlgeschlagen: ${error.message}`, type: 'error' }); }
  };
  const handleProposeAgendaItem = async (description) => { if (!selectedMeeting) return; try { setNotification({ message: 'Sende Vorschlag...', type: 'info' }); await proposeAgendaItem(selectedMeeting.companyId, selectedMeeting.id, description); setNotification({ message: 'Vorschlag erfolgreich eingereicht!', type: 'success' }); } catch (error) { setNotification({ message: `Fehler: ${error.message}`, type: 'error' }); } };
  const handleReviewAgendaItem = async (proposalId, action) => { if (!selectedMeeting) return; try { const actionText = action === 'approve' ? 'genehmigt' : 'abgelehnt'; setNotification({ message: `Vorschlag wird ${actionText}...`, type: 'info' }); await reviewAgendaItem(selectedMeeting.companyId, selectedMeeting.id, proposalId, action); setNotification({ message: `Vorschlag erfolgreich ${actionText}!`, type: 'success' }); if (action === 'approve') { const meetingFromList = [...meetings, ...externalMeetings].find(m => m.id === selectedMeeting.id); if (meetingFromList) await loadMeetingData(meetingFromList, 'form'); } } catch (error) { setNotification({ message: `Fehler: ${error.message}`, type: 'error' }); } };
  const handleCreateTranslationJob = async (meetingId, targetLanguage, translatorEmail) => { if (!companyId) return; try { setNotification({ message: `Erstelle Übersetzungsauftrag für ${targetLanguage.toUpperCase()}...`, type: 'info' }); const result = await createTranslationJobCallable({ companyId: companyId, meetingId: meetingId, targetLanguage: targetLanguage, translatorEmail: translatorEmail }); setNotification({ message: `Auftrag ${result.data.vorgangsNummer} erfolgreich gesendet!`, type: 'success' }); } catch (error) { setNotification({ message: `Fehler: ${error.message}`, type: 'error' }); } };
  const handleCancelTranslationJob = async (meetingId, targetLanguage) => { if (!companyId) return; try { setNotification({ message: `Storniere Auftrag für ${targetLanguage.toUpperCase()}...`, type: 'info' }); await cancelTranslationJobCallable({ companyId: companyId, meetingId: meetingId, targetLanguage: targetLanguage }); setNotification({ message: `Auftrag erfolgreich storniert.`, type: 'success' }); } catch (error) { console.error("Fehler beim Stornieren:", error); setNotification({ message: `Fehler: ${error.message}`, type: 'error' }); throw error; } };
  const handleRegisterUserAndSendEmail = useCallback(async (userData) => {
      const result = await registerUserCallable(userData);
      if (result && result.data && userData.email) {
          try {
              await sendPasswordResetEmail(auth, userData.email);
              setNotification({ 
                  message: `Benutzer ${userData.email} angelegt. Einladungs-E-Mail wurde gesendet.`, 
                  type: 'success' 
              });
          } catch (error) {
              console.error("Fehler beim Senden der Einladungs-Mail:", error);
              setNotification({ 
                  message: `Benutzer angelegt, aber E-Mail-Versand fehlgeschlagen: ${error.message}`, 
                  type: 'warning' 
              });
          }
      }
      return result;
  }, []);

  const renderContent = () => {
      if (!isAuthReady || isLoading) return <div className="text-center text-xl mt-20">{isLoading ? 'Erstelle Nummer...' : 'Lade...'}</div>;
      if (view === 'translate' && token) { return <TranslationPortal token={token} setNotification={setNotification} onHomeClick={handleGoToDashboard} />; }
      if (isGuestView) {
        if (guestError) return <div className="text-center text-xl text-red-500 mt-20 p-8">{guestError}</div>;
        if (guestMeeting) {
          const combinedAgenda = hydrateI18nAgenda(guestMeeting.agenda, guestMeeting);
          const hydratedGuestMeeting = { ...guestMeeting, agenda: hydrateTasksInAgenda(combinedAgenda, guestTasks) };
          return (<div><div className="p-4 bg-blue-100 dark:bg-blue-900/50 rounded-xl mb-6 text-center"><h3 className="font-bold">Gast-Ansicht</h3><p className="text-sm">Dies ist eine schreibgeschützte Ansicht. <a href="https://www.meetform.de" className="text-blue-600 font-semibold hover:underline">Registrieren Sie sich bei Meetform</a>!</p></div><MeetingView meeting={hydratedGuestMeeting} isGuest={true} allTasks={guestTasks} setNotification={setNotification} companyUsers={companyUsers} onUpdateParticipantStatus={() => {}} agendaProposals={[]} currentLang={guestMeeting.originalLanguage || 'de'} onLanguageChange={() => {}} onTranslationRequest={() => {}} /></div>);
        }
        return <div className="text-center text-xl mt-20">Protokoll wird geladen...</div>;
      }
      if (isRedirecting) return <div className="text-center text-xl mt-20">Wird umgeleitet...</div>;
      if (permissionError) return <div className="text-center text-xl text-red-500 mt-20 p-8">{permissionError}</div>;
      if (!userId) return ( <PublicDashboard setShowAuthModal={setShowAuthModal} setShowRequestModal={setShowRequestModal} setAuthMode={setAuthMode} /> );
      if (view === 'superAdmin' && isSuperAdmin) return <SuperAdminDashboard setNotification={setNotification} createCompanyCallable={createCompanyCallable} setSuperAdminClaimCallable={setSuperAdminClaimCallable} getAllCompaniesAndUsersCallable={getAllCompaniesAndUsersCallable} registrationRequests={registrationRequests} />;
      if (!companyId && !isSuperAdmin) return <div className="text-center text-xl text-red-500 mt-20">Keine Firma gefunden.</div>;

      switch(view) {
          case 'list': return <MeetingList meetings={meetings} externalMeetings={externalMeetings} onSelectMeeting={handleViewMeeting} onEditMeeting={handleSelectMeeting} onCreateNew={handleCreateNew} onDeleteMeeting={handleDeleteMeeting} onDuplicateMeeting={handleDuplicateMeeting} userNotifications={userNotifications} userId={userId} companyId={companyId} setNotification={setNotification} />;
          case 'form':
              if (isLoadingMeeting) return <div className="text-center text-xl mt-20">Lade Protokoll-Daten...</div>;
              return selectedMeeting ? <MeetingForm ref={meetingFormRef} meeting={selectedMeeting} onSave={handleSaveMeeting} onCancel={handleBackToList} setNotification={setNotification} currentUserId={userId} currentCompanyId={companyId} companyUsers={companyUsers} teams={teams} allTasks={tasks} onSendInvites={handleSendInvites} onSyncAttendees={handleSyncAttendees} onUpdateParticipantStatus={handleUpdateParticipantStatus} agendaProposals={agendaProposals} projectTopics={projectTopics} onProposeAgendaItem={handleProposeAgendaItem} onReviewAgendaItem={handleReviewAgendaItem} currentLang={currentLang} onCreateTranslationJob={handleCreateTranslationJob} onCancelTranslationJob={handleCancelTranslationJob} /> : <div className="text-center mt-20">Lade Formular...</div>;
          case 'admin': return userRole === 'admin' ? (<div><AdminDashboard currentCompanyId={companyId} setNotification={setNotification} registerUserCallable={handleRegisterUserAndSendEmail} companyUsers={companyUsers} onEditUser={handleUpdateProfile} /><AdminTeamManagement companyUsers={companyUsers} teams={teams} companyId={companyId} setNotification={setNotification} /></div>) : <div className="text-center text-red-500">Keine Admin-Berechtigung.</div>;
          case 'tasks': return <TaskDashboard tasks={tasks} userId={userId} companyId={companyId} setNotification={setNotification} companyUsers={companyUsers} teams={teams} userRole={userRole} userProfile={userProfile} />;
          case 'projects': return <ProjectTopics companyId={companyId} user={{ uid: userId, ...userProfile }} companyUsers={companyUsers} />;
          case 'calendar': return (
              <CalendarPage 
                  meetings={meetings} 
                  externalMeetings={externalMeetings} 
                  tasks={tasks}
                  userId={userId} 
                  onSelectMeeting={(m, mode) => mode === 'form' ? handleSelectMeeting(m) : handleViewMeeting(m)}
                  onSelectTask={() => handleGoToTasks()} 
              />
          );

          // === NEUER PLATZHALTER FÜR ARTIKEL ===
        case 'artikel': return <Articles userId={userId} companyId={companyId} />;
          case 'einkauf': return <PurchasingModule companyId={companyId} userId={userId} />;
          case 'pricing': return <PricingView companyId={companyId} onComplete={(modules) => setNotification({message: "Modul erfolgreich gebucht: " + JSON.stringify(modules), type: "success"})} />;
          
          case 'view':
              if (isLoadingMeeting) return <div className="text-center text-xl mt-20">Lade Protokoll-Ansicht...</div>;
              return selectedMeeting ? <MeetingView meeting={selectedMeeting} onBackToList={handleBackToList} setNotification={setNotification} allTasks={tasks} userId={userId} companyId={companyId} companyUsers={companyUsers} onUpdateParticipantStatus={handleUpdateParticipantStatus} agendaProposals={agendaProposals} currentLang={currentLang} onLanguageChange={setCurrentLang} onTranslationRequest={handleCreateTranslationJob} /> : <div className="text-center mt-20">Lade Ansicht...</div>;
           case 'profile': return auth.currentUser ? <UserProfilePage user={auth.currentUser} userProfile={userProfile} onUpdateProfile={handleUpdateProfile} setNotification={setNotification} companyId={companyId} onBack={handleGoToDashboard} /> : <div className="text-center mt-20">Lade Profil...</div>;
          case 'dashboard':
          default: return <Dashboard userProfile={userProfile} tasks={tasks} userId={userId} onGoToTasks={handleGoToTasks} companyId={companyId} />;
      }
  };

  return (
    <div className={`flex h-screen bg-gray-50 dark:bg-gray-900 overflow-hidden text-gray-900 dark:text-white ${isLoading || isLoadingMeeting ? 'opacity-50 pointer-events-none' : ''}`}>
       <Notification message={notification.message} type={notification.type} onClose={() => setNotification({ message: '', type: null })} />
       {showAuthModal && <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} onRegister={handleRegister} onLogin={handleLogin} onPasswordReset={handlePasswordReset} setNotification={setNotification} initialMode={authMode} />}
       {showRequestModal && <CompanyRequestModal isOpen={showRequestModal} onClose={() => setShowRequestModal(false)} setNotification={setNotification} />}
       {showPrivacyModal && (<CustomModal isOpen={showPrivacyModal} onClose={() => setShowPrivacyModal(false)}><div className="prose dark:prose-invert max-w-none"><h3 className="text-xl font-bold mb-4">Datenschutzerklärung</h3><p>Platzhalter...</p><div className="mt-6 text-right"><button onClick={() => setShowPrivacyModal(false)} className="px-6 py-2 bg-blue-600 text-white rounded-lg">Schließen</button></div></div></CustomModal>)}

       {/* === SIDEBAR (FIXED/COLLAPSIBLE) === */}
       {userId && companyId && !isGuestView && (
         <>
           {isMobileMenuOpen && <div className="fixed inset-0 z-40 bg-black/50 lg:hidden backdrop-blur-sm" onClick={() => setIsMobileMenuOpen(false)} />}
           
           <aside className={`fixed lg:static z-50 h-full bg-white dark:bg-gray-800 transition-all duration-300 ease-in-out flex flex-col flex-shrink-0 lg:shadow-none overflow-hidden pt-0
              ${isMobileMenuOpen ? 'translate-x-0 w-64' : '-translate-x-full lg:translate-x-0'} 
              ${isSidebarCollapsed ? 'lg:w-20' : 'lg:w-64'}
           `}>
              <div className={`h-16 flex items-center flex-shrink-0 transition-all duration-300 ${isSidebarCollapsed && !isMobileMenuOpen ? 'justify-center px-0' : 'justify-start px-4'}`}>
                  <button 
                      className="p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors" 
                      onClick={toggleSidebar} 
                      title="Menü umschalten"
                  >
                      <IconMenu className="w-6 h-6" />
                  </button>
              </div>
              
              <nav className="flex-1 py-4 px-3 space-y-2 overflow-y-auto scrollbar-thin">
                  <NavItem icon={<IconDashboard/>} label={t('header.dashboard', 'Dashboard')} onClick={handleGoToDashboard} collapsed={isSidebarCollapsed && !isMobileMenuOpen} active={view === 'dashboard'} />
                  <NavItem icon={<IconProtocols/>} label={t('header.meetings', 'Protokolle')} onClick={handleGoToMeetingList} collapsed={isSidebarCollapsed && !isMobileMenuOpen} active={view === 'list' || view === 'view' || view === 'form'} />
                  <NavItem icon={<IconCalendar/>} label="Kalender" onClick={handleGoToCalendar} collapsed={isSidebarCollapsed && !isMobileMenuOpen} active={view === 'calendar'} />
                  <NavItem icon={<IconTasks/>} label={t('header.tasks', 'Aufgaben')} onClick={handleGoToTasks} collapsed={isSidebarCollapsed && !isMobileMenuOpen} active={view === 'tasks'} />
                  <NavItem icon={<IconProjects/>} label={t('header.projects', 'Projekte')} onClick={handleGoToProjects} collapsed={isSidebarCollapsed && !isMobileMenuOpen} active={view === 'projects'} />
                  <NavItem icon={<IconPackage/>} label="Artikel" onClick={handleGoToArtikel} collapsed={isSidebarCollapsed && !isMobileMenuOpen} active={view === 'artikel'} />

                  <div className="mt-4 mb-2 px-3">
                      {!isSidebarCollapsed || isMobileMenuOpen ? (
                          <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Erweiterungen</p>
                      ) : (
                          <div className="h-px bg-gray-200 dark:bg-gray-700 w-full"></div>
                      )}
                  </div>
                  <NavItem icon={<IconShoppingCart/>} label="Einkauf" onClick={handleGoToEinkauf} collapsed={isSidebarCollapsed && !isMobileMenuOpen} active={view === 'einkauf'} />

              </nav>

              <div className="p-3 flex flex-col space-y-2">
                 <div className={`flex items-center ${isSidebarCollapsed && !isMobileMenuOpen ? 'flex-col space-y-2 justify-center' : 'justify-between space-x-2'}`}>
                    <div className="flex bg-gray-100 dark:bg-gray-700/50 p-1 rounded-lg">
                        <button onClick={() => setCurrentLang('de')} className={`px-2 py-1 rounded text-xs font-bold transition-all ${currentLang === 'de' ? 'bg-white dark:bg-gray-600 shadow-sm' : 'opacity-50 hover:opacity-100'}`}>DE</button>
                        <button onClick={() => setCurrentLang('en')} className={`px-2 py-1 rounded text-xs font-bold transition-all ${currentLang === 'en' ? 'bg-white dark:bg-gray-600 shadow-sm' : 'opacity-50 hover:opacity-100'}`}>EN</button>
                    </div>
                    <button onClick={toggleDarkMode} className="p-2 rounded-full text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" title={isDarkMode ? "Hellmodus" : "Dunkelmodus"}>
                       {isDarkMode ? ( <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" /></svg> ) : ( <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" /></svg> )}
                    </button>
                 </div>
                 
                 <div className="relative group" ref={profileMenuRef}>
                    <button 
                        onClick={() => setIsProfileMenuOpen(prev => !prev)} 
                        className={`flex items-center w-full p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-all ${isSidebarCollapsed && !isMobileMenuOpen ? 'justify-center' : ''}`}
                    >
                         <img src={userPhotoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(auth.currentUser?.displayName || userEmail || 'U')}&background=random&color=fff&size=40`} alt="Profil" className="w-8 h-8 rounded-full object-cover" onError={(e) => { e.target.onerror = null; e.target.src=`https://ui-avatars.com/api/?name=${encodeURIComponent(userEmail || 'U')}&background=random&color=fff&size=40` }} />
                         <div className={`ml-3 text-left overflow-hidden transition-all duration-300 ${isSidebarCollapsed && !isMobileMenuOpen ? 'w-0 opacity-0 ml-0' : 'w-auto opacity-100'}`}>
                             <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{userProfile.firstName || 'Benutzer'}</p>
                             <p className="text-xs text-gray-500 truncate w-32">{userEmail}</p>
                         </div>
                    </button>

                    {isProfileMenuOpen && (
                        <div className={`absolute bottom-full mb-2 left-0 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-2xl z-50 animate-fadeIn ${isSidebarCollapsed && !isMobileMenuOpen ? 'left-full ml-2 bottom-0' : ''}`}>
                          <div className="px-4 py-3">
                            <p className="font-semibold truncate text-gray-900 dark:text-white">{auth.currentUser?.displayName || userProfile.firstName || 'Benutzer'}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{userEmail}</p>
                          </div>
                          <nav className="py-1">
                            <button onClick={() => { handleGoToProfile(); setIsProfileMenuOpen(false); }} className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700">{t('header.profile', 'Profil bearbeiten')}</button>
                            {userRole === 'admin' && (<button onClick={() => { handleGoToAdminDashboard(); setIsProfileMenuOpen(false); }} className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700">{t('header.admin', 'Admin-Dashboard')}</button>)}
                            {isSuperAdmin && (<button onClick={() => { handleGoToSuperAdminDashboard(); setIsProfileMenuOpen(false); }} className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700">{t('header.superAdmin', 'Super-Admin')}</button>)}
                            <div className="my-1"></div>
                            <button onClick={handleLogout} className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20">{t('header.logout', 'Abmelden')}</button>
                          </nav>
                        </div>
                    )}
                 </div>
              </div>
           </aside>
         </>
       )}

       {/* === CONTENT WRAPPER === */}
       <div className="flex-1 flex flex-col min-w-0 h-full relative">
          <header className="h-16 bg-white/80 dark:bg-gray-800/80 backdrop-blur-md flex items-center justify-between px-4 z-30 sticky top-0 flex-shrink-0">
              <div className="flex items-center">
                  {userId && companyId && !isGuestView && (
                    <button className="lg:hidden p-2 mr-3 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors" onClick={toggleSidebar} title="Menü umschalten">
                       <IconMenu className="w-6 h-6" />
                    </button>
                  )}
                  <span className="font-extrabold text-xl bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-600 cursor-pointer whitespace-nowrap" onClick={handleGoToDashboard}>
                      Meetform
                  </span>
              </div>
              <div className="flex items-center space-x-3 md:space-x-4 ml-auto">
                {userId && <NotificationBell userNotifications={userNotifications} userId={userId} setNotification={setNotification} />}
                {!userId && (
                    <div className="flex items-center space-x-2">
                      <button onClick={() => { setShowAuthModal(true); setAuthMode('login'); }} className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">{t('header.login', 'Anmelden')}</button>
                      {!companyId && <button onClick={() => setShowRequestModal(true)} className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow">{t('header.register', 'Firma registrieren')}</button>}
                    </div>
                )}
              </div>
          </header>

          <main className="flex-1 overflow-hidden flex flex-col relative bg-gray-50 dark:bg-gray-900">
             <div className={(view === 'form' || view === 'view' || isGuestView || view === 'einkauf') ? "w-full h-full flex flex-col" : "w-full h-full overflow-y-auto scrollbar-thin max-w-6xl mx-auto p-4 md:p-8"}>
                {renderContent()}
             </div>
          </main>
       </div>
    </div>
  );
}

export default App;